import { LocationQueryValue } from 'vue-router';
import { Marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import {
  AdpDocument,
  DocType,
  FileNameSearchResponse,
  Part,
  SurveyRequestState,
  UserDocument,
} from '@/types';
import { sanitize } from '@/utils/sanitize';
import {
  findSourceIndexForTypedCitation,
  parseAndAnimateSurveyNode,
  parseSurveyNode,
} from '@/utils/survey/domParser';

export const getDocTypesQueryParam = (
  docTypes: LocationQueryValue | Array<LocationQueryValue>,
): Array<DocType | 'user_document'> | [] => {
  if (!docTypes) return [];

  const docTypesQueryParam = Array.isArray(docTypes) ? docTypes : [docTypes];
  return docTypesQueryParam as Array<DocType | 'user_document'>;
};

export const getSpecifiedFilesQueryParam = (
  specifiedFiles: LocationQueryValue | Array<LocationQueryValue>,
): Array<FileNameSearchResponse> | [] => {
  if (!specifiedFiles) return [];

  const specifiedFilesQueryParam = (specifiedFiles as string[])?.map(file =>
    JSON.parse(file),
  ) as Array<FileNameSearchResponse>;
  return specifiedFilesQueryParam;
};

/**
 * スネークケース(バックエンドの命名規則) を キャメルケース(フロントエンドの命名規則) に変換する関数
 * @param str スネークケースの文字列
 * @returns キャメルケースの文字列
 */
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * オブジェクトのプロパティ名をキャメルケースに変換する関数
 * @param obj スネークケースのプロパティを持つオブジェクト
 * @param recursive 再帰的に変換するかどうかのフラグ
 * @returns キャメルケースのプロパティを持つオブジェクト
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertKeysToCamelCase = (obj: any, recursive = true): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item, recursive));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelCaseKey = toCamelCase(key);
      acc[camelCaseKey] = recursive
        ? convertKeysToCamelCase(obj[key], recursive)
        : obj[key];
      return acc;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as any);
  }
  return obj;
};

/**
 * サニタイズ追加設定
 */
export const markdownToHtmlOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags, // 'img'タグは含まれていない
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    a: ['href', 'target', 'style', 'class'],
  },
};

/**
 * marked.jsのRendererをカスタマイズ
 *
 * - 行単位で返ってきたデータを毎回全量でparseするため、結果が変わらないようにするためのカスタムrenderer
 * - li要素が複数ある場合に、1個目のli要素内が最初は`<p>`タグで囲まれず、2個目のli要素が追加されたタイミングで、
 *  `<p>`タグで囲まれてしまう問題を解消する
 *
 * 詳細ログ: https://anews.atlassian.net/browse/ANDEV-4736?focusedCommentId=40605
 */
const marked = new Marked();
const renderer = new marked.Renderer();
renderer.listitem = item => {
  const text = item.text.trim();
  return `<li>${marked.parse(text)}</li>\n`;
};
marked.setOptions({
  renderer,
});

/**
 * SurveyHistoryのmodel_responseのパース処理
 * @param modelResponse model_response
 * @param sources sources
 * @param ignoreNewlineOnlySpacing 改行文字列のみ場合に、donguri-uiの`spacingクラス`を適用したdivタグを追加しないフラグ
 * @param enableAnimation アニメーション有無
 * @returns パース結果
 */
export const parseModelResponse = (
  modelResponse: string,
  sources: Array<AdpDocument | UserDocument> = [],
  ignoreNewlineOnlySpacing = false,
  enableAnimation = true,
): Array<Part> => {
  const markdownToHtml = marked.parse(modelResponse) as string;
  const sanitizedHtml = sanitize(markdownToHtml, markdownToHtmlOptions);
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');
  if (enableAnimation) {
    const { animatedParts } = parseAndAnimateSurveyNode(
      doc.body,
      0,
      1,
      ignoreNewlineOnlySpacing,
      sources,
    );
    return animatedParts;
  } else {
    const { parts } = parseSurveyNode(
      doc.body,
      ignoreNewlineOnlySpacing,
      sources,
    );
    return parts;
  }
};

const typedCitationRegex = /\[\^([a-z_]+):([0-9A-Z]+)(?::(\d+))?\]/g; // 新引用形式: [^docType:id:page_number] (例: [^article:123456789:1])

export const formatMarkdownForCopy = (
  markdown: string,
  sources: Array<AdpDocument | UserDocument>,
): string => {
  return markdown.replace(
    typedCitationRegex,
    (match, docType, id: string, pageNumber?: string) => {
      const sourceIndex = findSourceIndexForTypedCitation(
        sources,
        docType as DocType | 'user_document',
        id,
        pageNumber,
      );
      if (sourceIndex === -1) {
        return match; // ソースが見つからない場合は元の文字列を返す
      }
      return `[${sourceIndex + 1}]`; // 1-based indexに変換
    },
  );
};

/**
 * SurveyRequestStateに特化した状態判定ヘルパー関数
 * @param currentState - 現在のrequestState
 * @param targetStates - 判定対象の状態配列
 * @param shouldInclude - trueの場合は配列に含まれるかを判定、falseの場合は含まれないかを判定
 * @returns boolean - 判定結果
 */
export const checkRequestState = (
  currentState: SurveyRequestState | undefined,
  targetStates: SurveyRequestState[],
  shouldInclude: boolean,
): boolean => {
  if (!currentState) return !shouldInclude; // undefinedの場合、配列に含まれないと判定

  const isIncluded = targetStates.includes(currentState);
  return shouldInclude ? isIncluded : !isIncluded;
};
